import type { MigrationStage } from '@detect/shared';
import type { ImportRequest } from '../../services/migration';

/** Pure helpers for the migration page (no React, no I/O). */

export const STAGE_LABELS: Record<MigrationStage, string> = {
  connect: 'Connect',
  import: 'Import',
  tune: 'Tune',
  silent: 'Silent',
  live: 'Live',
};

export const STAGE_HELP: Record<MigrationStage, string> = {
  connect: 'Not holding anything. The incumbent alerts; Detect is not involved yet.',
  import: 'Detect monitors what was imported, silently: alerts are recorded but nobody is paged by Detect.',
  tune: 'Silent. Mine the incumbent’s alert history for noisy, flappy or ignored alerts.',
  silent: 'Silent. Compare what Detect would have alerted with what the incumbent did, then cut over when ready.',
  live: 'Detect pages. The incumbent’s alerting is switched off for these devices.',
};

/** The next stage for the plain “move on” button. `silent → live` is the cutover (its own, confirmed action). */
export function nextStage(stage: MigrationStage): MigrationStage | null {
  switch (stage) {
    case 'connect': return 'import';
    case 'import': return 'tune';
    case 'tune': return 'silent';
    default: return null; // silent → live is the cutover; live is the end
  }
}

/** The one step back a plain button may take (a live scope is reversed, not stepped back). */
export function previousStage(stage: MigrationStage): MigrationStage | null {
  switch (stage) {
    case 'import': return 'connect';
    case 'tune': return 'import';
    case 'silent': return 'tune';
    default: return null;
  }
}

export const isSilentStage = (stage: MigrationStage): boolean => stage === 'import' || stage === 'tune' || stage === 'silent';

/** "123=col-a, 456=col-b" (or one per line) → `{ "123": "col-a", … }`; throws a readable Error on a bad line. */
export function parseCollectorMap(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const raw of text.split(/[\n,]+/)) {
    const line = raw.trim();
    if (line === '') continue;
    const m = /^(\d{1,12})\s*[=:]\s*(\S+)$/.exec(line);
    if (!m) throw new Error(`"${line}" is not "<LogicMonitor collector id> = <Detect collector id>"`);
    if (m[1] in out) throw new Error(`LogicMonitor collector ${m[1]} is mapped twice`);
    out[m[1]] = m[2];
  }
  return out;
}

/** "1, 2, 3" → [1,2,3]; throws a readable Error. */
export function parseIdList(text: string, what: string): number[] {
  const ids: number[] = [];
  for (const raw of text.split(/[\s,]+/)) {
    if (raw === '') continue;
    if (!/^\d{1,12}$/.test(raw)) throw new Error(`"${raw}" is not a ${what} id`);
    ids.push(Number(raw));
  }
  return [...new Set(ids)];
}

export type DeviceMode = 'all' | 'groups' | 'ids' | 'none';

export function buildImportRequest(f: {
  dryRun: boolean;
  mode: DeviceMode;
  idsText: string;
  includeWebsites: boolean;
  collectorMapText: string;
  clientId: string;
  afterDeviceId?: number;
  afterWebsiteId?: number;
}): ImportRequest {
  let devices: ImportRequest['devices'];
  if (f.mode === 'all') devices = { allDevices: true };
  else if (f.mode === 'groups') {
    const ids = parseIdList(f.idsText, 'device group');
    if (ids.length === 0) throw new Error('Enter at least one device group id');
    devices = { deviceGroupIds: ids };
  } else if (f.mode === 'ids') {
    const ids = parseIdList(f.idsText, 'device');
    if (ids.length === 0) throw new Error('Enter at least one device id');
    devices = { deviceIds: ids };
  }
  if (devices === undefined && !f.includeWebsites) throw new Error('Select devices and/or include websites');
  return {
    dryRun: f.dryRun,
    ...(devices ? { devices } : {}),
    includeWebsites: f.includeWebsites,
    collectorMap: parseCollectorMap(f.collectorMapText),
    ...(f.clientId !== '' ? { clientId: f.clientId } : {}),
    ...(f.afterDeviceId !== undefined ? { afterDeviceId: f.afterDeviceId } : {}),
    ...(f.afterWebsiteId !== undefined ? { afterWebsiteId: f.afterWebsiteId } : {}),
  };
}

export function selectorFromForm(f: { mode: 'all' | 'clients' | 'groups'; clientIds: string[]; groupsText: string }): unknown {
  if (f.mode === 'all') return { all: true };
  if (f.mode === 'clients') {
    if (f.clientIds.length === 0) throw new Error('Choose at least one client');
    return { clientIds: f.clientIds };
  }
  const groups = f.groupsText.split(/[\n,]+/).map((g) => g.trim()).filter((g) => g !== '');
  if (groups.length === 0) throw new Error('Enter at least one resource group');
  return { resourceGroups: [...new Set(groups)] };
}

export function describeSelector(sel: Record<string, unknown>): string {
  if (sel.all === true) return 'whole tenant';
  const parts: string[] = [];
  if (Array.isArray(sel.clientIds)) parts.push(`${sel.clientIds.length} client${sel.clientIds.length === 1 ? '' : 's'}`);
  if (Array.isArray(sel.resourceGroups)) parts.push(`groups: ${(sel.resourceGroups as string[]).join(', ')}`);
  if (Array.isArray(sel.resourceIds)) parts.push(`${sel.resourceIds.length} resource${sel.resourceIds.length === 1 ? '' : 's'}`);
  if (typeof sel.propertyExpression === 'string') parts.push(`expression: ${sel.propertyExpression}`);
  return parts.join(' · ') || '—';
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 90) return `${Math.round(seconds)} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1)} h`;
}

export const pct = (share: number): string => `${Math.round(share * 100)}%`;
